import type { AiChange } from '@roll1/shared';

function objectValue(value: unknown): Record<string, unknown> | undefined {
  if (value == null || value === '') return {};
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return undefined; }
  }
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function equal(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every(k => Object.hasOwn(right, k) && equal(left[k], right[k]));
}

/** Display projection only: undo always receives the unmodified server journal. */
export function summarizeChanges(changes: readonly AiChange[]): AiChange[] {
  const out: AiChange[] = [];
  const lastField = new Map<string, number>();
  const expanded = changes.flatMap(ch => {
    if (ch.entity.kind !== 'sheet' || ch.op !== 'update' || ch.field !== 'roleplay_json') return [ch];
    const before = objectValue(ch.before);
    const after = objectValue(ch.after);
    if (!before || !after) return [ch];
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
    if (keys.length === 0) return [ch];
    const fields = keys.filter(k => !equal(before[k], after[k]));
    return fields.map(field => ({ ...ch, field: `roleplay_json.${field}`, before: before[field], after: after[field] }));
  });
  for (const ch of expanded) {
    // These fields describe separate additions/equipment rows, not replacement of a single value.
    const additive = ['item', 'equipped', 'material', 'links'].includes(ch.field ?? '');
    const key = JSON.stringify([ch.entity.kind, ch.entity.id, ch.entity.parentId, ch.field]);
    const index = lastField.get(key);
    const previous = index === undefined ? undefined : out[index];
    const merge = ch.op === 'update' && ch.field && ch.entity.id && !additive && previous
      && previous.undone === ch.undone && previous.kept === ch.kept && previous.irreversible === ch.irreversible
      && equal(previous.after, ch.before);
    if (merge && index !== undefined) {
      out[index] = { ...previous, after: ch.after, entity: ch.entity };
    } else {
      if (ch.op !== 'update') lastField.clear();
      else lastField.set(key, out.length);
      out.push(ch);
    }
  }
  return out;
}
